// PortGroups.js
// Some board sockets are exposed under several port aliases that are physically
// the SAME socket. Connecting a component to ANY alias occupies the WHOLE group,
// so no other component can use any alias in that group.
//
// Example (Poki): E, E1 and E2 are one socket. If a Digital_3_Pin_Output is wired
// to E1, then E, E1 and E2 are all considered taken — an IMU (which uses E) can no
// longer connect. The (F, F1, F2) trio behaves the same way.
//
// This is the single source of truth for that grouping. Keyed by connectedDevice;
// a device with no entry has no grouped ports (every port stands alone).
export const PORT_GROUPS = {
    Poki: [
        ['E', 'E1', 'E2'],
        ['F', 'F1', 'F2'],
    ],
    // woofKit shares Poki's grouped E/F sockets.
    woofKit: [
        ['E', 'E1', 'E2'],
        ['F', 'F1', 'F2'],
    ],
}

// Return the alias group a port belongs to (including the port itself). Ports that
// aren't grouped return a single-element list so callers can treat every port the
// same way.
export const getPortGroup = (device, port) => {
    const groups = PORT_GROUPS[device] || []
    return groups.find((g) => g.includes(port)) || [port]
}

// True if `port` — or ANY alias in its group — is already taken in PortConnections.
// PortConnections[alias] is the connected component ({type, index}) or null.
export const isPortGroupOccupied = (device, port, PortConnections) =>
    !!PortConnections &&
    getPortGroup(device, port).some((alias) => PortConnections[alias])

// Special hint look for a grouped socket: only ONE alias — the one the user is meant
// to connect to right now — is drawn, as a single circle sitting at the group's fixed
// position (`fixedPos`), plus that alias's own decorative path to the board. This way
// the user gets an unambiguous picture of exactly which socket is in play.
// Board-relative coords (bibox is added at render time).
//   circles[].pos — legacy per-alias centre; kept for the label lookup. The active
//                   circle is always drawn at `fixedPos`, not at its own pos.
//   fixedPos      — the single centre every alias's circle is drawn at.
//   paths         — map of alias → polyline points for that alias's wire; first point
//                   is where it leaves the blob, last is where it reaches the board/bus.
//                   The path for the currently-active alias is the one that renders.
// Tune coords visually. Keyed by device, then by the group's first alias.
export const GROUP_BLOBS = {
    Poki: {
        E: {
            members: ['E', 'E1', 'E2'],
            circles: [
                { key: 'E', label: 'E', pos: [-30, 165] },
                { key: 'E1', label: 'E1', pos: [10, 165] },
                { key: 'E2', label: 'E2', pos: [50, 165] },
            ],
            fixedPos: [50, 165],
            paths: {
                E: [
                    [52, 165],
                    [75, 165],
                    [75, 154],
                    [92, 154],
                    [92, 158],
                    [122, 158],
                    [92, 158],
                    [92, 150],
                    [122, 150],
                ],
                E1: [
                    [10, 153],
                    [122, 153],
                ],
                E2: [
                    [52, 165],
                    [92, 165],
                    [92, 158],
                    [122, 158],
                ],
            },
        },
        F: {
            members: ['F', 'F1', 'F2'],
            circles: [
                { key: 'F', label: 'F', pos: [300, 267] },
                { key: 'F1', label: 'F1', pos: [340, 267] },
                { key: 'F2', label: 'F2', pos: [380, 267] },
            ],
            fixedPos: [300, 267],
            paths: {
                F: [
                    [295, 267],
                    [255, 267],
                    [255, 171],
                    [245, 171],
                    [245, 166],
                    [235, 166],
                    [245, 166],
                    [245, 173],
                    [235, 173],
                ],
                F1: [
                    [340, 267],
                    [255, 267],
                    [255, 166],
                    [235, 166],
                ],
                F2: [
                    [380, 282],
                    [380, 297],
                    [252, 297],
                    [252, 173],
                    [235, 173],
                ],
            },
        },
    },
}
// woofKit shares Poki's grouped-socket blob hints (E/E1/E2 and F/F1/F2).
GROUP_BLOBS.woofKit = GROUP_BLOBS.Poki

// The blob whose group contains `port`, or undefined when `port` isn't grouped.
export const getGroupBlob = (device, port) => {
    const blobs = GROUP_BLOBS[device]
    if (!blobs) return undefined
    return Object.values(blobs).find((b) => b.members.includes(port))
}
